import { useEffect, useLayoutEffect, useRef, useState } from 'react';

interface UseInViewOptions {
  /** Reveal once and stop observing (default true). */
  once?: boolean;
  /** IntersectionObserver rootMargin (default reveals slightly before). */
  margin?: string;
}

/**
 * Tracks whether an element has entered the viewport.
 *
 * Elements that are ALREADY visible at mount are reported as
 * `inView: true, animate: false` from a pre-paint layout check — they must
 * render in their final state with no entrance animation, otherwise every
 * route change would stack a reveal on top of the view transition.
 * Only elements below the fold (revealed later by scrolling) animate.
 *
 * Falls back to visible-without-animation when IntersectionObserver is
 * unavailable (jsdom/tests) or the user prefers reduced motion, so content
 * is never stuck hidden.
 */
export function useInView<T extends HTMLElement>({
  once = true,
  margin = '0px 0px -10% 0px',
}: UseInViewOptions = {}) {
  const ref = useRef<T | null>(null);
  const noMotionEnv =
    typeof IntersectionObserver === 'undefined' ||
    typeof window.matchMedia !== 'function' ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [state, setState] = useState<{ inView: boolean; animate: boolean }>(
    () => ({ inView: noMotionEnv, animate: false }),
  );

  // Pre-paint: anything already in the viewport renders final-state instantly.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    if (rect.top < viewportHeight && rect.bottom > 0) {
      // Functional update: a no-op when already inView (same state bails out).
      setState((current) =>
        current.inView ? current : { inView: true, animate: false },
      );
    }
    // Run only on mount: later visibility is the observer's job.
  }, []);

  useEffect(() => {
    if (state.inView && once) return;
    const element = ref.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setState({ inView: true, animate: true });
            if (once) observer.disconnect();
          } else if (!once) {
            setState({ inView: false, animate: true });
          }
        }
      },
      { rootMargin: margin },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [state.inView, once, margin]);

  return { ref, inView: state.inView, animate: state.animate };
}
